import axios from "axios";
import { toast } from "sonner";
import { getAdminToken, clearAdminToken } from "./adminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 90000 });

// Attach admin token to any /cursedgold45/* request when present.
client.interceptors.request.use((config) => {
  if (config.url && config.url.startsWith("/cursedgold45")) {
    const t = getAdminToken();
    if (t) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Surface a friendly toast on 429, then let the original promise reject so the
// page-level handler can still react.
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 429) {
      const retry = err.response.headers?.["retry-after"];
      const detail = err.response.data?.error || err.response.data?.detail;
      toast.error("Rate limit hit", {
        description: `${detail || "Slow down a bit"}${retry ? ` · retry in ${retry}s` : ""}`,
      });
    } else if (
      err?.response?.status === 401 &&
      err?.config?.url?.startsWith("/cursedgold45") &&
      !err?.config?.url?.endsWith("/cursedgold45/login")
    ) {
      clearAdminToken();
    }
    return Promise.reject(err);
  }
);

function httpError(status, data) {
  const err = new Error(data?.detail || data?.error || `HTTP ${status}`);
  err.response = { status, data };
  return err;
}

function parseSseChunk(buffer, onEvent) {
  const parts = buffer.split("\n\n");
  const rest = parts.pop();
  for (const part of parts) {
    const line = part.split("\n").find((l) => l.startsWith("data: "));
    if (line) onEvent(JSON.parse(line.slice(6)));
  }
  return rest;
}

async function streamOnce(cveId, forceRefresh, onEvent) {
  const res = await fetch(`${API}/cve/analyze/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ cve_id: cveId, force_refresh: forceRefresh }),
  });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => ({}));
    if (res.status === 429) {
      const retry = res.headers.get("retry-after");
      toast.error("Rate limit hit", {
        description: `${data.error || data.detail || "Slow down a bit"}${retry ? ` · retry in ${retry}s` : ""}`,
      });
    }
    throw httpError(res.status, data);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let final = null;
  const handle = (evt) => {
    if (evt.type === "result" || evt.type === "error") final = evt;
    else onEvent(evt);
  };
  try {
    while (!final) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer = parseSseChunk(buffer + decoder.decode(value, { stream: true }), handle);
    }
  } catch {
    // Dropped connection: the caller reconnects and the server replays progress.
  }
  reader.cancel().catch(() => {});
  return final;
}

// Live-progress analysis over Server-Sent Events. The ingress closes requests at
// ~60s, so a dropped stream reconnects and re-attaches to the same server job.
export async function analyzeCVEStream(cveId, forceRefresh = false, onEvent = () => {}) {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    let final = null;
    try {
      final = await streamOnce(cveId, attempt === 0 && forceRefresh, onEvent);
    } catch (err) {
      const status = err?.response?.status;
      const retryable = !status || status >= 500;
      if (attempt === 0 || !retryable) throw err;
    }
    if (final?.type === "result") return final.data;
    if (final?.type === "error") throw httpError(final.status, { detail: final.detail });
    await new Promise((r) => setTimeout(r, 1000));
  }
  throw new Error("Lost connection to the analysis. Please try again.");
}

export async function getDetections(cveId, forceRefresh = false) {
  const res = await client.post(
    `/cve/${encodeURIComponent(cveId)}/detections`,
    null,
    { params: { force_refresh: forceRefresh } }
  );
  return { status: res.status, data: res.data };
}

export async function getAssessmentKit(cveId, forceRefresh = false) {
  const res = await client.post(
    `/cve/${encodeURIComponent(cveId)}/assessment-kit`,
    null,
    { params: { force_refresh: forceRefresh } }
  );
  return { status: res.status, data: res.data };
}

export async function getJob(jobId) {
  const res = await client.get(`/jobs/${encodeURIComponent(jobId)}`);
  return res.data;
}

export async function pollJob(jobId, { intervalMs = 2500, timeoutMs = 180000 } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const job = await getJob(jobId);
    if (job.status === "done" || job.status === "failed" || job.status === "cancelled") {
      return job;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Job polling timed out");
}

export async function cancelJob(jobId) {
  const res = await client.delete(`/jobs/${encodeURIComponent(jobId)}`);
  return res.data;
}

// ---------------- Admin ----------------

export async function adminLogin(email, password) {
  const res = await client.post("/cursedgold45/login", { email, password });
  return res.data;
}

export async function adminMe() {
  const res = await client.get("/cursedgold45/me");
  return res.data;
}

export async function adminStats() {
  const res = await client.get("/cursedgold45/stats");
  return res.data;
}

export async function adminCves(limit = 50) {
  const res = await client.get("/cursedgold45/cves", { params: { limit } });
  return res.data;
}

export async function adminSboms(limit = 50) {
  const res = await client.get("/cursedgold45/sboms", { params: { limit } });
  return res.data;
}

export async function adminJobs(limit = 50) {
  const res = await client.get("/cursedgold45/jobs", { params: { limit } });
  return res.data;
}

export async function adminChangePassword(currentPassword, newPassword) {
  const res = await client.post("/cursedgold45/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
}

export async function adminRecomputeExploitability() {
  const res = await client.post("/cursedgold45/recompute-exploitability");
  return res.data;
}

// Triggers a browser download of the branded PDF for the given CVE.
// We open the URL directly so the browser handles the file save dialog.
export function exportCvePdfUrl(cveId) {
  return `${API}/cve/${encodeURIComponent(cveId)}/export.pdf`;
}

export async function fetchWhatsBroken(dateStr) {
  const path = dateStr ? `/whats-broken/${dateStr}` : "/whats-broken";
  const res = await client.get(path);
  return res.data;
}

export async function adminVisits(days = 30) {
  const res = await client.get("/cursedgold45/visits", { params: { days } });
  return res.data;
}

export async function adminFeedback(limit = 100) {
  const res = await client.get("/cursedgold45/feedback", { params: { limit } });
  return res.data;
}

export async function adminListApiKeys() {
  const res = await client.get("/cursedgold45/api-keys");
  return res.data;
}

export async function adminCreateApiKey(label, ratePerMin = 60) {
  const res = await client.post("/cursedgold45/api-keys", {
    label,
    rate_limit_per_min: ratePerMin,
  });
  return res.data;
}

export async function adminRevokeApiKey(keyId) {
  const res = await client.delete(
    `/cursedgold45/api-keys/${encodeURIComponent(keyId)}`
  );
  return res.data;
}

// ---------------- Public feedback ----------------

export async function getCaptcha() {
  const res = await client.get("/feedback/captcha");
  return res.data;
}

export async function submitFeedback(payload) {
  const res = await client.post("/feedback", payload);
  return res.data;
}

export async function scanSBOM(content, name, format) {
  const res = await client.post("/sbom/scan", { content, name, format });
  return res.data;
}

export function exportSbomPdfUrl(scanId) {
  return `${API}/sbom/${encodeURIComponent(scanId)}/export.pdf`;
}

export async function exportVEX(name, decisions) {
  const res = await client.post("/sbom/vex", { name, decisions });
  return res.data;
}
